import React, { useState, useContext, useEffect, createRef, useRef } from 'react';
import QuoteBuilderList from 'components/layout_and_quote_builder/quote_builder/QuoteBuilderList.jsx';
import { systemItemsThicknessFilter } from 'components/frank/utils/materialCompatibility.js';
import useCommitOnPause from 'components/common/useCommitOnPause';
import FormFieldFactory from './FormFieldFactory.jsx';
import FormTitle from './FormTitle.jsx';
import ResultsTable from './ResultsTable.jsx';
import FormActions from './FormActions.jsx';
import MessageAlerts from './MessageAlerts.jsx';
import SystemCheckbox from './SystemCheckbox.jsx';
import { PostRequest as OriginalPostRequest, GetRequest, Tooltip } from 'components/common';
import { applySaveValueInto, seedSaveValueIntoFields } from 'components/common/saveValueInto';
import { attributeProperty } from 'components/common/attributeProperty';
import { Form, Button } from 'semantic-ui-react';
import { useForm  } from "react-hook-form";
import hash from "object-hash";
import { useCache } from './CacheContext';
import RequestQueueManager from 'components/layout_and_quote_builder/utils/RequestQueueManager.js';

// Create a dedicated queue manager with concurrency = 1 for this component
const quoteBuilderQueueManager = new RequestQueueManager(1);

// QueuedPostRequest wrapper using the dedicated queue manager
// PostRequest now returns a Promise, so we just wrap it and enqueue it
const QueuedPostRequest = (props) => {
  const requestFn = () => OriginalPostRequest(props);
  return quoteBuilderQueueManager.enqueue(requestFn);
};

// The part types printed by default -- exactly those whose Baskı (printing side) dropdown is shown, mirroring
// the server's UserQuoteNavSection::PART_TYPES_PRINTED_BY_DEFAULT.
const PART_TYPES_PRINTED_BY_DEFAULT = ['carton', 'carton_lamination', 'cover', 'corrugated_cardboard_flexo', 'corrugated_cardboard_flexo_on_carton'];

// SINGLE SOURCE OF TRUTH for a nav section's default values -- what windowNavSection() falls back to when
// window.nav_sections carries no entry for this section (or none has loaded yet), so the three fallback
// branches below can't drift apart. `lamination` and `printing_side` mirror the server's
// UserQuoteNavSection.default_lamination_for / default_printing_side_for: a carton_lamination section is always
// laminated, and the printed part types default to a one_side printing -- keep the two in sync.
const navSectionDefaults = (partType) => ({
  lamination: partType === 'carton_lamination' ? 'one_side' : 'none',
  printing_side: PART_TYPES_PRINTED_BY_DEFAULT.includes(partType) ? 'one_side' : 'none',
  printing_block: 'one_block',
  wastage_unit: 'sheet',
  wastage_count: 0
});

// Fill any field a resolved nav section is missing from navSectionDefaults (part-type aware), in place.
const applyNavSectionDefaults = (navSection) => {
  const defaults = navSectionDefaults(navSection.part_type);
  Object.keys(defaults).forEach((key) => {
    if(navSection[key] === undefined) { navSection[key] = defaults[key]; }
  });
  return navSection;
};

const QuoteBuilderForm = ({ config, readOnly, disabled, showingState, linkToSettings, beforeSave, afterSave, afterCancel, editStateId, editedKlass, options, readOnlyValues, section_uuid, item, extra_title, navSection, columnTitle, colIndex, rowIndex, getExtraItems, navSectionColumnType, context, hideDeleteFromList }) => {
  const default_fetch_path = '/quote-builder/user_unit_prices/:id';
  const default_fetch_system_path = '/quote-builder/user_unit_prices_system/:id';
  const default_update_path = '/quote-builder/user_unit_prices/update';
  const default_create_path = '/quote-builder/user_unit_prices/create/:key'

  // A UserUnitPriceAttribute reference field's matching machine TAB config (by key), if this form has one -- the
  // SAME config the settings machine tab hands the library picker (library_enabled, listing_properties, the type
  // and dimension filters, the picker title). The field uses it so "create new" opens the full library picker,
  // rather than a hand-copied subset that drops the facets. Undefined for a form/field with no such tab (materials
  // etc.), which then keep the plain manual-create flow. `config` here is the FORM config (its tabs), not a field.
  const attributeTabConfigFor = (fieldKey) => (config.tabs || []).find(
    (tab) => tab.form_type === 'UserUnitPriceAttribute' && tab.key === fieldKey
  );

  const USER_UNIT_PRICE_CHANGED_WITH_ITEM_EVENT_KEY = 'quote_builder.user_unit_price_changed_with_item';

  const STATE_LIST = 'list';
  const STATE_NEW  = 'new';
  const STATE_EDIT = 'edit';
  const STATE_COPY_FROM_SYSTEM  = 'copy_from_system';
  const VALUE_NOT_AVAILABLE = 'not-available'
  const VALUE_NEW = "new-attribute";
  const keyFor = (config) => hash(config);
  const v = (key) => { return values[key] }
  const va = (key) => { return values['user_unit_price_attribute.' + key] }

  // Registry of loaded UserUnitPriceAttributes (machines, ...) by id; each picker reports its list
  // up via onAttributesLoaded so settings.yml expressions can read a SELECTED attribute's property
  // (not just its id, which is all va() gives). Mirrors components/quote_builder/QuoteBuilderForm.
  // useRef, so populating it does NOT trigger a re-render: fine for the current vap() user (a hidden
  // field evaluated fresh at save), but a *visible* field keyed on vap() would not re-render when the
  // picker list finishes loading until the next unrelated render.
  const attributesById = useRef({});
  const registerAttributeProperties = (loadedAttributes) => {
    (loadedAttributes || []).forEach((attribute) => {
      if (attribute && attribute.id != undefined) { attributesById.current[attribute.id] = attribute; }
    });
  };
  // makeVap(bag): build vap('printing_machine', 'printing_type') bound to a value bag, so a visible_if
  // resolves the SELECTED attribute's property from the same bag it is evaluated against — live values
  // on save, the freshly fetched record on seed. See components/common/attributeProperty.js.
  const makeVap = (bag) => (attributeKey, propertyName) => attributeProperty(bag, attributesById.current, attributeKey, propertyName);
  // vap resolves against the live `values`, read LAZILY at call time (values is declared further below):
  // binding eagerly here would hit a temporal-dead-zone ReferenceError under strict const semantics
  // (jest) and capture an undefined `values` where const is transpiled to var (the browser build).
  // Passed to FormFieldFactory for the field-render visible_if evals.
  const vap = (attributeKey, propertyName) => makeVap(values)(attributeKey, propertyName);
  const onlyUnique = (value, index, self) => { return self.indexOf(value) === index; }

  const is_any_new_value = (list) => { return list.indexOf(VALUE_NEW) > -1 }
  const is_not_new_value = (list) => { return list.indexOf(VALUE_NEW) == -1 }
  const is_not_new_value_va = (list) => { return is_not_new_value(list.map((key) => va(key))) }

  // Use cache context
  const { state: cacheState, dispatch: cacheDispatch } = useCache();

  const [submitInProgress, setSubmitInProgress] = useState(false);
  // const [hasFile, setHasFile] = useState('');
  const has_file = useRef('');
  const zeroPriceValueFor = (attributeName) => {
    const attributeConfig = config.attributes.find((candidate) => candidate.name === attributeName);
    const precision = attributeConfig != undefined && attributeConfig.precision != undefined ? attributeConfig.precision : 2;
    return (0).toFixed(precision);
  };

  // A multi-select (checkboxes) attribute added after a record was created is absent from that
  // record and arrives null from the server. Seed its default_value (typically all options) so it
  // renders like a new record rather than empty; an explicit saved selection arrives as an array
  // (even an empty one) and is used as-is, so unchecking and saving still sticks.
  const multiSelectDefaultFor = (attributeName) => {
    const attributeConfig = config.attributes.find((candidate) => candidate.name === attributeName);
    if (attributeConfig != undefined && attributeConfig.form_type === 'checkboxes' && attributeConfig.default_value != undefined) {
      return attributeConfig.default_value;
    }
    return undefined;
  };

  const defaultValueByFormType = (config) => {
    if(config.form_type == 'price' && config.precision == 4) { return '0.0000' }

    return {
      'UserUnitPriceAttribute': '',
      'UserUnitPrice': 'not-available',
      'select': '',
      'checkboxes': [],
      'boolean': false,
      'hidden': '',
      'length': '0.00',
      'number': '0.00',
      'integer': '0',
      'price': '0.00',
      'price_with_precision_4': '0.0000',
      'currency': (window.user_quote && window.user_quote.currency) || window.default_currency
    }[config.form_type]
  };


  const windowNavSection = () => {
    // Guard against undefined navSection prop -- no navSection means no part_type to derive from, so
    // lamination falls to "none" (navSectionDefaults with no part type).
    if (!navSection) {
      console.warn('[QuoteBuilderForm] navSection prop is undefined');
      return navSectionDefaults();
    }

    // Guard against undefined window.nav_sections
    if (!window.nav_sections || !Array.isArray(window.nav_sections)) {
      console.warn('[QuoteBuilderForm] window.nav_sections is not available, using navSection prop');
      return applyNavSectionDefaults({ ...navSection });
    }

    let result = undefined;

    // Try 1: Match by index/sort_order (primary method after Layer 1 fix)
    if(navSection.index !== undefined) {
      for(let i = 0; i < window.nav_sections.length; i++) {
        if(window.nav_sections[i].sort_order === navSection.index ||
           window.nav_sections[i].index === navSection.index) {
          result = window.nav_sections[i];
          break;
        }
      }
    }

    // Try 2: If index doesn't match, try label (fallback for stale props)
    if(result === undefined && navSection.label !== undefined) {
      for(let i = 0; i < window.nav_sections.length; i++) {
        if(window.nav_sections[i].label === navSection.label) {
          result = window.nav_sections[i];
          console.warn(`[QuoteBuilderForm] navSection matched by label instead of index (${navSection.index}). This may indicate stale props.`);
          break;
        }
      }
    }

    // Try 3: If still not found, use the navSection prop itself with defaults
    if(result === undefined) {
      console.warn(
        `[QuoteBuilderForm] windowNavSection could not find match for navSection.label="${navSection.label}", ` +
        `navSection.index=${navSection.index}, user_quote_id=${window.user_quote_id}. ` +
        `Using provided navSection with default values.`
      );

      // Track this as a potential bug for monitoring
      trackEvent('quote_builder_nav_section_not_found', {
        navSection_label: navSection.label,
        navSection_index: navSection.index,
        user_quote_id: window.user_quote_id,
        window_nav_sections_count: window.nav_sections.length,
        window_nav_sections_labels: window.nav_sections.map(ns => ns.label).join(','),
        window_nav_sections_indexes: window.nav_sections.map(ns => ns.index || ns.sort_order).join(',')
      });

      // Return navSection prop with safe defaults
      result = { ...navSection };
    }

    // Ensure required fields have defaults (single source of truth: navSectionDefaults, part-type aware).
    return applyNavSectionDefaults(result);
  }

  const partType = () => {
    return navSection.part_type;
  }

  const d = (varName) => {
    if(config.dynamic_variables == undefined) { return; }

    for(let i = 0; i < config.dynamic_variables.length; i++ ){
      if(config.dynamic_variables[i].name == varName) {
        return eval(config.dynamic_variables[i].value)
      };
    }
  }

  const userUnitPriceAttributes = useRef([]);

  const addToUsetUnitPriceAttributes = (name) => {
    if (userUnitPriceAttributes.current == undefined) { userUnitPriceAttributes.current = []; }
    userUnitPriceAttributes.current.push(name);
    userUnitPriceAttributes.current = userUnitPriceAttributes.current.filter(onlyUnique);
  }

  const useLayout = () => {
    if(options == undefined) { return }
    if(config.key == undefined) { return }
    if(options.layout == undefined) {
      switch (config.key) {
        case "hot_stamping":
        case "printing":
        case "cellophane":
        case "glueing":
        case "material":
        case "lamination":
        case "lamination":
        case "coating_labor":
        case "v_cut_labor":
        case "material":
        case "transport":
        case "profit_margin":
          options.layout = options.layout_printing;
          break;

       case "guillotine_cutting":
       case "cutting":
       case "flexo_printing_and_die_cutting":
          options.layout = options.layout_diecutting;
        break;

        default:
          console.log(`Sorry, we dont know what to do for ${config.key}.`);
      }
    }
  }

  const getInitialValueFromCache = (name) => {
    if(config == undefined || columnTitle == undefined || navSection == undefined) { return undefined; }
    const cache = cacheState.user_quote_section_properties_cached;
    if(cache == undefined || cache[navSection.label] == undefined) { return undefined; }
    let searchKey = t(config.title) + "_" + columnTitle;
    if (cache[navSection.label][searchKey] != undefined) {
      return cache[navSection.label][searchKey][name];
    }

    return undefined;
  }

  const initialValues = () => {
    useLayout();

    let values = {
      user_quote_id: window.user_quote_id,
      nav_section_column_type: navSectionColumnType,
      section_uuid: section_uuid,
      id: '',
      price_attributes: [],
      user_exchange_rates: {},
    };

    if(window.user_quote != undefined && window.user_quote.layout_part_group_windows.length > 0) {
      for(let i=0; i < window.user_quote.layout_part_group_windows.length; i++) {
       if(window.user_quote.layout_part_group_windows[i].value != undefined) {
         values[window.user_quote.layout_part_group_windows[i].attribute_name] = window.user_quote.layout_part_group_windows[i].value;
       } else {
         values[window.user_quote.layout_part_group_windows[i].attribute_name] = 'not-set';
       }
      }
    }

    if(navSection != undefined) {
      values.nav_section = navSection;
      values.row_index = rowIndex;
      values.item = item;
    }

    config.attributes.map( (config) => {
      if(config.form_type == 'price') {
        values[ 'price_attributes' ] = values[ 'price_attributes' ].concat([config.name]);
      }

      // A field the chosen manufacturing line seeds (Frank own-line pricing, `_line_seed` in the payload): the
      // CHOSEN line's value OUTRANKS a value cached from an earlier pick, so the section opens priced on the
      // line just chosen rather than a stale one. Undefined for any other field / flow -> falls through unchanged.
      const lineSeed = options && options._line_seed ? options._line_seed[config.name] : undefined;

      if(lineSeed != undefined) {
        values[config.name] = lineSeed;

      } else if(getInitialValueFromCache(config.name) != undefined) {
        values[config.name] = getInitialValueFromCache(config.name);

      } else {
        if (config.default_value != undefined) {
          values[config.name] = config.default_value;
        } else {
          if (config.default_value_eval != undefined) {
            values[config.name] = eval(config.default_value_eval);
          } else {
            if(config.default_value_in_values != undefined) {
              values[config.name] = values[config.default_value_in_values];
            } else {
              values[config.name] = defaultValueByFormType(config);
            }
          }
        }
      }
    });

    return values;
  }

  const [componentShowingState, setComponentShowingState] = useState(showingState == undefined ? (config.list.enabled ? STATE_LIST : STATE_NEW) : showingState);
  const [fetchInProgress, setFetchInProgress] = useState(false);
  const [childSubFormOpen, setChildSubFormOpen] = useState(false);
  const [itemIdBeingEdited, setItemIdBeingEdited] = useState(editStateId);
  // Which record's saved values are currently seeded into the form. Compared against the record being
  // edited (see editDataLoading below) to show a spinner while an edit form's data is still loading,
  // instead of flashing empty/default (invalid) values. Mirrors the legacy quote_builder tree.
  const [seededEditId, setSeededEditId] = useState(null);
  const [postOnChangeResponse, setPostOnChangeResponse] = useState({});
  const [hidden, setHidden] = useState(false);
  const [values, setValues] = useState(initialValues());
  const [inCompleteMessage, setInCompleteMessage] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);
  const outputCurrencies = useRef([]);
  const {register, handleSubmit, clearErrors, trigger, formState: { errors: allErrors }} = useForm();

  // Track which fields the user has interacted with, and whether a save has been attempted, so
  // that validation errors are only DISPLAYED on fields the user has reached. Validation itself
  // still runs over all fields (save is blocked by trigger()); this only gates what is shown.
  const [touchedFields, setTouchedFields] = useState({});
  const [saveAttempted, setSaveAttempted] = useState(false);

  const scrollTop = () => {
    if(window.QuoteBuilderFormScrollTop) {
      $("html, body").animate({ scrollTop: 100 }, "slow");
    }
  }

  const setValue = (id, value) => {
    setValues( (previous) => {
      if (typeof value === 'function') {
        previous[id] = value(previous);
      } else {
        previous[id] = value;
      }
      return { ...previous };
    })
  }

  // Record a field as touched. Driven by the form-level onBlur, which bubbles every child input's
  // blur (event.target.name is the blurred field). Keeps premature errors off untouched fields,
  // e.g. picking a colour count must not flag the still-untouched price field further down.
  const markFieldTouched = (name) => {
    if (!name) { return; }
    setTouchedFields((previous) => previous[name] ? previous : { ...previous, [name]: true });
  };

  // Only the errors the user should currently see: a field's error shows once that field is
  // touched, or once a save has been attempted (which reveals everything at once). The single
  // errors={errors} prop below consumes this filtered set, so the gating applies form-wide.
  const errors = {};
  for (const name in allErrors) {
    if (saveAttempted || touchedFields[name]) {
      errors[name] = allErrors[name];
    }
  }

  // Start each form context (new record, editing a different record, switching list/edit) with a
  // clean slate so previously touched fields / a prior save attempt don't carry over and reveal
  // errors on a freshly opened form.
  useEffect(() => {
    setTouchedFields({});
    setSaveAttempted(false);
  }, [componentShowingState, itemIdBeingEdited])

  const getDefaultFetchPath = () => {
    if(editedKlass == "UserUnitPriceAttribute") {
      return `/quote-builder/user_unit_price_attributes/properties/${itemIdBeingEdited}`
    }

    return (config.fetch_path || default_fetch_path).replace(':id', itemIdBeingEdited);
  }

  const getDefaultUpdatePath = () => {
    if(editedKlass == "UserUnitPriceAttribute") {
      return `/quote-builder/user_unit_price_attributes/update_properties/${itemIdBeingEdited}`
    }

    return config.submit.update_path || default_update_path;
  }

  const getFetchEnabled = () => {
    if(editedKlass == "UserUnitPriceAttribute") { return true }
    return config.fetch_enabled;
  }


  const save = async () => {
    if(disabled || readOnly) { return };
    setSubmitInProgress( () => true )

    setFetchInProgress(true);

    if(beforeSave != undefined) beforeSave();

    let url;

    switch(componentShowingState) {
      case STATE_NEW:
        url = (config.submit.create_path || default_create_path).replace(':key', config.key)
        break;

      case STATE_COPY_FROM_SYSTEM:
        url = (config.submit.create_path || default_create_path).replace(':key', config.key)
        break;

      case STATE_EDIT:
        url = getDefaultUpdatePath()
        break;

      default:
        throw 'invalid componentShowingState';
    }

    if(await trigger()) {

      applySaveValueInto(config.attributes, values, makeVap);

      if(has_file.current != undefined && has_file.current != '') {
        values.file_fields = [ has_file.current ] ;
        values[ has_file.current ] = $(`[name='${has_file.current }']`)[0].files[0]
      }

      // On Frank (/mcc) the carton bobbin fields are shown to every user (settings.yml visible_if reads
      // window.frank_mode), so the create/update must also ACCEPT their values. carton_coil_available?
      // honours this flag, which the page sets only on Frank -- everywhere else it is undefined and the
      // admin/example-account gate is unchanged.
      if(window.frank_mode) {
        values.frank_mode = true;
      }

      QueuedPostRequest({
        url: url,
        data: values,
        callback: (data) => {
          setItemIdBeingEdited(null);
          setFetchInProgress(false);
          document.dispatchEvent(new CustomEvent('quote_builder.user_unit_price_saved', {}) );
          if(afterSave != undefined) {
            afterSave(data.id);
          } else {
            setComponentShowingState(STATE_LIST);
          }

         setSubmitInProgress( () => false )
        },
        400: (data) => {
          setErrorMessage(data.errors);
          setItemIdBeingEdited(null);
          setFetchInProgress(false);
          setSubmitInProgress( () => false );
        }
      }).catch((error) => {
        console.error('[QuoteBuilderForm save] Unhandled error:', error);
        setFetchInProgress(false);
        setSubmitInProgress(() => false);
        setItemIdBeingEdited(null);
      })
    } else {
      setSubmitInProgress( () => false );
    }
  }

  const cancel = () => {
    clearErrors();
    setItemIdBeingEdited(null);
    setComponentShowingState(STATE_LIST);
    if(afterCancel != undefined) {
      afterCancel();
    }
  }

  const windowValue = () => {
    let key = 'window_type__default';

    if(item && item.layout_part_group_code != '' && item.layout_part_group_code != undefined && item.layout_part_group_code != null) {
      key = `window_type_${item.layout_part_group_code}`;
    }

    return values[key];
  }

  // requestGetExtraItems
  const requestGetExtraItemsRef = useRef([]);
  const requestGetExtraItems = (key_value) => {
    requestGetExtraItemsRef.current.push([key_value, section_uuid]);
  }

  const processGetExtraItemRequests = () => {
    for(let i = 0; i < requestGetExtraItemsRef.current.length; i++) {
      let args = requestGetExtraItemsRef.current[i];
      getExtraItems(args[0], args[1], values, item, options);
    }

    requestGetExtraItemsRef.current = [];
    if(config.get_extra_items_on_change) {
      getExtraItems({ key: config.get_extra_items_key, value: config.get_extra_items_value }, eval(config.get_extra_items_uuid_eval) , values, item, options);
    }
  }

  //requestPublishItemOnChange
  const requestPublishItemOnChangeRef = useRef([]);
  const requestPublishItemOnChange = (msg) => {
    requestPublishItemOnChangeRef.current.push(msg);
  }

  const processPublishItemOnChange = () => {
    for(let i=0; i < requestPublishItemOnChangeRef.current.length; i++) {
      let message = requestPublishItemOnChangeRef.current[i];
      let publishItemKey = message.publish_item_key != undefined ? message.publish_item_key : USER_UNIT_PRICE_CHANGED_WITH_ITEM_EVENT_KEY;

      message.values = values;
      message.item = item;
      message.nav_section = navSection;

      // console.log({key: publishItemKey, message: message})
      document.dispatchEvent(new CustomEvent(publishItemKey, { detail: message } ));
    }
    requestPublishItemOnChangeRef.current = [];
  }

  const formFieldFactory = (config, index) => {
    return <FormFieldFactory
      config={config}
      libraryTabConfig={attributeTabConfigFor(config.key)}
      index={index}
      values={values}
      options={options}
      setValue={setValue}
      readOnlyValues={readOnlyValues}
      setFetchInProgress={setFetchInProgress}
      setChildSubFormOpen={setChildSubFormOpen}
      disabled={disabled}
      readOnly={readOnly}
      register={register}
      errors={errors}
      context={context}
      item={item}
      navSection={navSection}
      requestPublishItemOnChange={requestPublishItemOnChange}
      requestGetExtraItems={requestGetExtraItems}
      navSectionColumnType={navSectionColumnType}
      windowValue={windowValue()}
      addToUsetUnitPriceAttributes={addToUsetUnitPriceAttributes}
      has_file={has_file}
      showLongDescriptions={showLongDescriptions}
      d={d}
      v={v}
      va={va}
      VALUE_NOT_AVAILABLE={VALUE_NOT_AVAILABLE}
      VALUE_NEW={VALUE_NEW}
      is_not_new_value={is_not_new_value}
      is_not_new_value_va={is_not_new_value_va}
      vap={vap}
      onAttributesLoaded={registerAttributeProperties}
      dataSourcesEnabled={window.quote_builder_data_sources_enabled}
      itemIdBeingEdited={itemIdBeingEdited}
    />;
   }

   const myFormRef = useRef();

   const submit = async () => {
    // Reveal every field's error on a save attempt (this is the real Save path: the button is
    // type='button' with onClick={submit}, so an invalid save would otherwise show nothing).
    setSaveAttempted(true);
    if(await trigger()) {
      myFormRef.current.handleSubmit(save);
    }
  };


  const onPostOnChangeCompletedEnabled = useRef(false);

  const onPostOnChangeCompleted = (data) => {
    document.dispatchEvent(new CustomEvent('quote_builder.section_result_updated', { detail: section_uuid }));

    if(data.user_quote_section_properties_cached != undefined) {
      // Use cache context dispatch instead of direct window mutation
      cacheDispatch({ 
        type: 'UPDATE_CACHE', 
        payload: data.user_quote_section_properties_cached 
      });
    }

    if(data.user_quote != undefined) {
      window.user_quote = data.user_quote;
    }

    if(data.nav_sections != undefined) {
      window.nav_sections = data.nav_sections;
    }
  }

  const anyUserUnitPriceAttributeIsSet = useRef(false);
  const valuesHash = useRef();

  const delay = 200;
  let debounceTimer;
  const postOnChange = async () => {
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(() => {
      postOnChangeAction();
    }, delay);
  };

  // Typed values commit when typing pauses, and on Enter, instead of waiting for focus to leave the
  // field. Straight to postOnChangeAction rather than through postOnChange, so the pause here is the
  // whole delay rather than this one plus that one's.
  const { noteUserChange, commitNow } = useCommitOnPause({
    values: values,
    enabled: config.post_on_change,
    commit: () => postOnChangeAction(),
  });

  const postOnChangeActionInProgress = useRef(false);
  const postOnChangeAction = async () => {

    if(disabled || hidden || postOnChangeActionInProgress.current || fetchInProgress) { return };
    if(config.post_on_change_needs_user_quote_id && window.user_quote_id == undefined) { return };
    if(valuesHash.current != undefined && hash(values) == valuesHash.current) { return }

    postOnChangeActionInProgress.current = true;
    setFetchInProgress( () => true );

    setInCompleteMessage(null);
    setErrorMessage(null);
    setPostOnChangeResponse([]);

    if(anyUserUnitPriceAttributeIsSet.current == false && userUnitPriceAttributes.current != undefined) {
      for(let i = 0; i < userUnitPriceAttributes.current.length; i++) {
        if(values[userUnitPriceAttributes.current[i]] != VALUE_NOT_AVAILABLE) {
          anyUserUnitPriceAttributeIsSet.current = true;
        }
      }
    }

    if(anyUserUnitPriceAttributeIsSet.current == false && userUnitPriceAttributes.current != undefined && userUnitPriceAttributes.current.length > 0) {
      setTimeout(processPublishItemOnChange, 250);
      setFetchInProgress(() => false );
      postOnChangeActionInProgress.current = false;
      if(window.user_quote_finalised){ setHidden( () => true) }
      return;
    }

    if(await trigger()) {
      let valuesToPost = { ...values };
      valuesHash.current = hash(values);
      if(has_file.current != undefined && has_file.current != '') {
        // todo
      }

      QueuedPostRequest({
        url: config.post_on_change_path,
        data: valuesToPost,
        callback: (data) => {

          if(data.results != undefined) {
            setPostOnChangeResponse(data.results);
            outputCurrencies.current = data.results.currencies;
            if(window.user_quote_finalised && data.results.total_price_in_pence == 0){
              setHidden( () => true)
            }

            if(data.results.total_price_in_pence > 0){
              emitEvent("total_price_in_pence_set", {
                label: navSection.label,
                total_price_in_pence: data.results.total_price_in_pence
              } )
            }
          }

          // Pricing a section changes its automatic fire adeti; the server recomputed it, so push the new
          // value to the nav section's (read-only, automatic) wastage field rather than waiting for a reload.
          if(data.nav_section_auto_wastage != undefined && data.nav_section_auto_wastage != null){
            emitEvent("nav_section_wastage_recomputed", {
              label: data.nav_section_auto_wastage.label,
              wastage_count: data.nav_section_auto_wastage.wastage_count
            } )
          }

          onPostOnChangeCompleted(data);
          onPostOnChangeCompletedEnabled.current = true;
          processPublishItemOnChange();
          processGetExtraItemRequests();
          setFetchInProgress(() => false );
          postOnChangeActionInProgress.current = false;
        },
        422: (data) => {
          setInCompleteMessage(data.message);
          setPostOnChangeResponse({line_item_prices: []});
          if(onPostOnChangeCompletedEnabled.current) { onPostOnChangeCompleted(data); }
          processPublishItemOnChange();
          processGetExtraItemRequests();
          setFetchInProgress(() => false );
          postOnChangeActionInProgress.current = false;
          if(window.user_quote_finalised){ setHidden( () => true) }
        },
        400: (data) => {
          setErrorMessage(data.message);
          setPostOnChangeResponse({line_item_prices: []});
          if(onPostOnChangeCompletedEnabled.current) { onPostOnChangeCompleted(data); }
          processPublishItemOnChange();
          processGetExtraItemRequests();
          setFetchInProgress(() => false );
          postOnChangeActionInProgress.current = false;
        }
      }).catch((error) => {
        console.error('[QuoteBuilderForm postOnChange] Unhandled error:', error);
        setFetchInProgress(() => false);
        postOnChangeActionInProgress.current = false;
        setPostOnChangeResponse({line_item_prices: []});
      });
    } else {
      // Nothing was sent, so nothing is in flight. Without this the section stays wearing
      // `disabledForm` -- greyed out, and blocked from ever committing again by the in-progress guard
      // at the top of this method -- for as long as the page is open. It is easy to land on while
      // editing: a field emptied on the way to a new value fails its NonEmptyValidator, and the
      // commit that follows a pause in typing arrives right in that gap.
      setFetchInProgress(() => false);
      postOnChangeActionInProgress.current = false;
    }
  }

  const validateOnChange = () => {
    if(disabled) { return };
    setInCompleteMessage(null);
    setErrorMessage(null);

    trigger();
  }


  const disabledIf = () => {
    if(submitInProgress) { return true }
    if(childSubFormOpen) { return true }
    return (config.submit.disabled_if != undefined && eval(config.submit.disabled_if))
  }

  const sectionClearEnabled = useRef(false);

  const onKeyUpWrapped = (e) => {
    // sectionClearEnabled.current = true;
    postOnChange()
  }

  const onBlurWrapped = (e) => {
    // sectionClearEnabled.current = true;
    postOnChange()
  }
  const cookieNameforLongDescription = 'quote_builder_settings_show_long_descriptions';
  const [showLongDescriptions, setShowLongDescriptions] = useState(true);

  const handleToggleLongDescriptions = () => {
    setShowLongDescriptions(!showLongDescriptions);
    setCookie(cookieNameforLongDescription, !showLongDescriptions, 365)
  }


  const [localDisabled, setLocalDisabled] = useState(false);
  const [localDisabledReason, setLocalDisabledReason] = useState('');

  const [isSystem, setIsSystem] = useState(false);
  const handleIsSystemChange = (e) => {
    QueuedPostRequest({
      url: `/quote-builder/user_unit_prices/update-is-system`,
      data: {
        id: itemIdBeingEdited,
        is_system: e.target.checked
      },
      callback: (data) => {
        setIsSystem(() => {return data.is_system})
      }
    }).catch((error) => {
      console.error('[QuoteBuilderForm handleIsSystemChange] Unhandled error:', error);
    })
  }

  const shouldFetchForEditMode = () => {
    return getFetchEnabled() && itemIdBeingEdited > 0 && componentShowingState == STATE_EDIT;
  }

  const shouldFetchForCopyFromSystemMode = () => {
    return config.fetch_enabled && itemIdBeingEdited > 0 && componentShowingState == STATE_COPY_FROM_SYSTEM;
  }

  const fetchDataForEditMode = () => {
    const idBeingFetched = itemIdBeingEdited;
    setFetchInProgress(true);

    GetRequest({
      url: getDefaultFetchPath(),
      callback: (data) => {
        data.price_attributes = initialValues()['price_attributes'];
        setFetchInProgress(true);

        for (const [key, value] of Object.entries(data)) {
          // A price that arrives null (a machine-only record saved before pricing existed)
          // must not lock the input in its "Loading..." state: seed an editable zero instead.
          if (value === null && data.price_attributes.includes(key)) {
            setValue(key, zeroPriceValueFor(key));
          } else if (value === null && multiSelectDefaultFor(key) != undefined) {
            setValue(key, multiSelectDefaultFor(key));
          } else {
            setValue(key, value);
          }
        }

        seedSaveValueIntoFields(config.attributes, data, setValue, makeVap);

        if(data.is_system != undefined && setIsSystem != undefined) {
          setIsSystem(data.is_system);
        }

        scrollTop();
        setFetchInProgress(false);
        setSeededEditId(idBeingFetched);
      },
      // On any failure mark this record resolved so the form shows rather than spinning forever.
      onError: () => {
        setFetchInProgress(false);
        setSeededEditId(idBeingFetched);
      }
    })
  }

  const fetchDataForCopyFromSystemMode = () => {
    setFetchInProgress(true);

    GetRequest({
      url: default_fetch_system_path.replace(':id', itemIdBeingEdited),
      callback: (data) => {
        data.price_attributes = initialValues()['price_attributes'];
        setFetchInProgress(true);

        for (const [key, value] of Object.entries(data)) {
          if(config.mark_system.system_attribute_names.includes(key)) {
            setValue(key, value);
          }
        }

        setValue('copied_from_id', itemIdBeingEdited);

        scrollTop();
        setFetchInProgress(false);

        setTimeout(() => {
          setComponentShowingState(() => { return STATE_LIST});
          setTimeout(() => {setComponentShowingState(() => { return STATE_COPY_FROM_SYSTEM});}, 2)
        }, 2)
      }
    })
  }

  // useEffect starts............................................................................
  useEffect(() => {
    if(disabled || readOnly) { return };

    if(shouldFetchForEditMode()) {
      fetchDataForEditMode();
      return;
    }

    if(shouldFetchForCopyFromSystemMode()) {
      fetchDataForCopyFromSystemMode();
      return;
    }

  }, [ itemIdBeingEdited ]);

  // useEffect( () => {
  //   if(disabled || readOnly) { return };

  //   if(componentShowingState == STATE_NEW) {
  //     if(window.QuoteBuilderFormScrollTop) { scrollTop() }
  //     setValues( { ...initialValues() } );
  //   }
  // }, [componentShowingState])

  useEffect(() => {
    const keyDownHandler = event => {
      if (event.key === 'Enter') {
        event.preventDefault();
      }
    };

    document.addEventListener('keydown', keyDownHandler);

    return () => {
      document.removeEventListener('keydown', keyDownHandler);
    };
  }, []);

  useEffect(() => {
   if(disabled) { return };

   if(config.post_on_change && values['updated_at'] != undefined){
    postOnChange();
   }
  }, [values['updated_at']]);

  useEffect(() => {
    let event = new CustomEvent('quote_builder.output_currencies_changed', { "detail": outputCurrencies.current });
    document.dispatchEvent(event);

  }, [outputCurrencies.current]);

  useEffect(() => {
    if(config.post_on_change) {
      let listener = function(e) {
        setValue('user_exchange_rates', (previous) => {
          previous['user_exchange_rates'][e.detail.key] = e.detail.value;
          return { ...previous['user_exchange_rates'] };
        });

        setValue('updated_at', Date.now());
      };

      document.addEventListener('quote_builder.currency_exchange_rate_changed', listener);
      return () => { document.removeEventListener('quote_builder.currency_exchange_rate_changed', listener);}
    }
  }, []);

  if(config.disabled_if != undefined) {
    useEffect(() => {
      if( eval(config.disabled_if) ) {
        setLocalDisabled( (_prev) => true )
        setLocalDisabledReason( (_prev) => t(config.disabled_if_reason) );
      } else {
        setLocalDisabled( (_prev) => false )
        setLocalDisabledReason( (_prev) => null);
      }
    }, [values])
  }

  // Section-level hidden_if: unlike disabled_if (greyed out with a reason), a hidden section is
  // removed from view entirely while staying mounted, so its hidden fields keep listening and it
  // keeps posting on changes (letting its calculator clear its saved total server-side).
  const [localHidden, setLocalHidden] = useState(false);
  if(config.hidden_if != undefined) {
    useEffect(() => {
      if( eval(config.hidden_if) ) {
        setLocalHidden( (_prev) => true )
      } else {
        setLocalHidden( (_prev) => false )
      }
    }, [values])
  }
  // useEffect ends................................................................

  if( (disabled || localDisabled) && window.user_quote_finalised ) { return }
  if( hidden && window.user_quote_finalised ) { return }

  // Show a spinner (instead of the form) while an edit form's saved values are still loading, so the
  // empty/default (invalid) values never flash. Derived in render (never a post-paint frame) and
  // gated to only when a fetch will actually run. Mirrors the legacy quote_builder tree.
  const editDataLoading =
    componentShowingState == STATE_EDIT &&
    !disabled &&
    !readOnly &&
    getFetchEnabled() &&
    itemIdBeingEdited > 0 &&
    String(seededEditId) !== String(itemIdBeingEdited);

  // Narrow the frequently-used ("Sık kullanılanlar") system list to the boards the section can use (hard cardboard
  // to those within tolerance of the drawing thickness), from the one shared rule -- so this tree and the /mcc
  // picker never diverge. options.material_thickness_in_mm is the dieline's malzeme in mm (0/absent -> unfiltered).
  const filterSystemItems = systemItemsThicknessFilter(config.key, options && options.material_thickness_in_mm);

  return (
    <div className={`${config.class_name != undefined ? config.class_name : ''} ${(fetchInProgress || disabled || localDisabled) ? 'disabledForm' : ''}`} style={localHidden ? { display: 'none' } : undefined} data-section-uuid={section_uuid} id={section_uuid}>
      {config.list.enabled && componentShowingState == STATE_LIST && <QuoteBuilderList config={config} setComponentShowingState={setComponentShowingState} setItemIdBeingEdited={setItemIdBeingEdited} hideDeleteFromList={hideDeleteFromList} filterSystemItems={filterSystemItems}/> }

      {editDataLoading && (
        <div className="quote-builder-form-loading" style={{ padding: '40px 20px', textAlign: 'center', color: '#888' }}>
          <i className="fa fa-spinner fa-spin fa-2x"></i>
          <div style={{ marginTop: '12px' }}>{t('quote_builder.please_wait')}</div>
        </div>
      )}

      {!editDataLoading && (componentShowingState == STATE_NEW || componentShowingState == STATE_EDIT || componentShowingState == STATE_COPY_FROM_SYSTEM) && (
        <Form
          onSubmit={handleSubmit(save, () => setSaveAttempted(true))}
          ref={myFormRef}
          onInput={noteUserChange}
          onKeyDown={(event) => { if (event.key === 'Enter') { commitNow(); } }}
          onBlur={(event) => { markFieldTouched(event.target.name); (config.post_on_change ? onBlurWrapped : validateOnChange)(event); }}>
         <table className="table table-striped table-bordered-outside quote_builder_table">
          <tbody>
            <FormTitle 
              config={config}
              componentShowingState={componentShowingState}
              extra_title={extra_title}
              localDisabledReason={localDisabledReason}
              showLongDescriptions={showLongDescriptions}
              handleToggleLongDescriptions={handleToggleLongDescriptions}
            />

            {config.attributes.map((attr, index) => (
              <React.Fragment key={attr.name || index}>
                {formFieldFactory(attr, index)}
              </React.Fragment>
            ))}

            <FormActions
              config={config}
              componentShowingState={componentShowingState}
              submitInProgress={submitInProgress}
              childSubFormOpen={childSubFormOpen}
              disabledIf={disabledIf}
              submit={submit}
              cancel={cancel}
              linkToSettings={linkToSettings}
            />

              {((config.post_on_change && postOnChangeResponse != undefined && postOnChangeResponse.line_item_prices != undefined && postOnChangeResponse.line_item_prices.length > 0) || inCompleteMessage || errorMessage) && (
                <tr>
                  <td> </td>
                  <td colSpan="1">
                     <ResultsTable 
                       config={config}
                       postOnChangeResponse={postOnChangeResponse}
                     />
                  </td>
                </tr>
              )}

            <SystemCheckbox 
              config={config}
              componentShowingState={componentShowingState}
              isSystem={isSystem}
              handleIsSystemChange={handleIsSystemChange}
            />

          </tbody>
        </table>

        <MessageAlerts 
          inCompleteMessage={inCompleteMessage}
          errorMessage={errorMessage}
        />

      </Form>
      )}
    </div>
  )
}

export default QuoteBuilderForm;
